import { useCallback, useEffect, useState } from "react";
import { api, TERMS_NEEDED_EVENT, type TermsAsked, termsAsked } from "./api.ts";
import { BeforeWeStart } from "./BeforeWeStart.tsx";
import { early, forgetEarly } from "./early.ts";
import { failureOf } from "./failure.ts";
import { askedByAddress, openFeedback } from "./feedback.ts";
import { GettingStarted } from "./GettingStarted.tsx";
import { useLoad } from "./hooks.ts";
import { JoinPage } from "./JoinPage.tsx";
import { NewVersion } from "./NewVersion.tsx";
import { useKeepDevice } from "./Notifications.tsx";
import { useAgain } from "./offline.ts";
import { Failed, Opening, settle } from "./opening.tsx";
import { Profile } from "./Profile.tsx";
import { openRedeem, redeemByAddress } from "./Redeem.tsx";
import { navigate, type Route, useRoute } from "./router.ts";
import { ShareLink } from "./ShareLink.tsx";
import { Button } from "./souvenir.ts";
import { TripPage } from "./TripPage.tsx";
import { Trips } from "./Trips.tsx";
import { Message } from "./ui.tsx";
import { useDeviceZone } from "./zone.ts";

export function App() {
  const route = useRoute();
  // Bumped after changing my photo or name, to load me again.
  const [version, setVersion] = useState(0);
  // Asked as the scripts started (main.tsx), the first time.
  const me = useLoad(() => early("me", api.me), [version, useAgain()]);
  // Getting started on this page: my first AI connected while it showed (it stays, saying so, until
  // tapped through), and tapped through (on with the app, me loaded again behind it).
  const [connectedHere, setConnectedHere] = useState(false);
  const [startedHere, setStartedHere] = useState(false);
  const onConnected = useCallback(() => setConnectedHere(true), []);
  const onStarted = useCallback(() => {
    setStartedHere(true);
    setVersion((v) => v + 1);
  }, []);
  // 16 or older, and the Terms, once (BeforeWeStart.tsx): asked by the refusal of who I am, or of any call while the
  // page is open (the Terms changed meanwhile). Until it's done, it's every page.
  const [held, setHeld] = useState<TermsAsked | null>(null);
  useEffect(() => {
    const ask = (e: Event) => setHeld((e as CustomEvent<TermsAsked>).detail);
    addEventListener(TERMS_NEEDED_EVENT, ask);
    return () => removeEventListener(TERMS_NEEDED_EVENT, ask);
  }, []);
  const asked = held ?? (me.state === "failed" ? termsAsked(me.error) : null);
  // Done: on to where the page was going. At /welcome, where it was sent from (an AI's allow page, a page of the
  // server's); elsewhere, the page itself, who I am loaded again.
  // Asked while a page was open (a call refused, the Terms changed meanwhile): the page opens again, as it would
  // have, rather than keep what that refusal left on it.
  const ready = me.state === "ready";
  const agreed = useCallback(async () => {
    if (route.name === "welcome" && (await goOn(route))) return;
    if (ready) return location.reload();
    setHeld(null);
    setVersion((v) => v + 1);
  }, [route, ready]);
  useKeepDevice(me.state === "ready");
  useDeviceZone(me.state === "ready" ? me.data.time_zone : undefined);
  // Until an AI is connected or connecting is skipped for now (kept for the account), every page is getting
  // started (a join link asks to join first). Skipped, every page is as for anyone, and the profile still
  // connects one (features/getting-started.md, "Skip for now").
  const starting =
    me.state === "ready" &&
    !startedHere &&
    (connectedHere || (me.data.ais.length === 0 && !me.data.tips_seen.includes("skip_connecting")));
  // What was loaded early (main.tsx) is for the page shown first: it has taken it by now.
  useEffect(() => {
    if (me.state !== "loading") forgetEarly();
  }, [me.state]);
  // Whatever the first page turns out to be, the sunrise never outstays it (opening.tsx).
  useEffect(settle, []);
  // Report this on one of the server's own pages (the wrong gate, a page that failed) comes here by its address: the
  // form, over the page, once I'm known; the address goes back to the page's own.
  const signedIn = me.state === "ready";
  useEffect(() => {
    if (!signedIn) return;
    const asked = askedByAddress();
    if (!asked) return;
    navigate(asked.rest, { replace: true });
    openFeedback(asked.ask);
  }, [signedIn]);
  // A code's link (/redeem/CODE, features/paying.md "Free Trip Passes from codes") opens Your trips with the sheet that
  // redeems it, the code filled in, once I'm known (and past the screen after signing up); the address goes back to /,
  // so a one-off code isn't kept in it.
  useEffect(() => {
    if (!signedIn) return;
    const asked = redeemByAddress();
    if (!asked) return;
    navigate("/", { replace: true });
    openRedeem(asked.code);
  }, [signedIn]);
  if (me.state === "loading") return <Opening />;
  if (asked)
    return (
      <>
        <BeforeWeStart
          key={asked.version}
          asked={asked}
          onAgreed={agreed}
          onChanged={() => {
            setHeld(null);
            setVersion((v) => v + 1);
          }}
        />
        <NewVersion floating />
      </>
    );
  if (me.state === "failed")
    return (
      <Failed
        failure={failureOf(me.error) ?? "not-answering"}
        what="tripmo"
        again={() => setVersion((v) => v + 1)}
        retrying={me.retrying === true}
      />
    );
  // The screen's own address, done already (another device, a moment ago): on to where it was sent from.
  if (route.name === "welcome") return <OnFromWelcome route={route} onHome={() => setVersion((v) => v + 1)} />;
  // A trip shows a new version where its live banner goes; the other pages, floating at the foot.
  if (route.name === "join")
    return (
      <>
        <JoinPage
          key={route.code}
          code={route.code}
          me={me.data}
          starting={starting}
          onConnected={onConnected}
          onStarted={onStarted}
        />
        <NewVersion floating />
      </>
    );
  if (starting)
    return (
      <>
        <GettingStarted me={me.data} onConnected={onConnected} onFinish={onStarted} />
        <NewVersion floating />
      </>
    );
  if (route.name === "profile")
    return (
      <>
        <Profile me={me.data} how={route.how} connect={route.connect} onChanged={() => setVersion((v) => v + 1)} />
        <NewVersion floating />
      </>
    );
  if (route.name === "share")
    return (
      <>
        <ShareLink me={me.data} url={route.url} text={route.text} title={route.title} />
        <NewVersion floating />
      </>
    );
  if (route.name === "trip")
    return (
      <TripPage
        key={route.trip}
        me={me.data}
        trip={route.trip}
        item={route.item}
        view={route.view}
        attach={route.attach}
        list={route.list}
        full={route.full}
        settings={route.settings}
        rate={route.rate}
        today={route.today}
        reading={route.research}
        about={route.about}
      />
    );
  return (
    <>
      <Trips me={me.data} />
      <NewVersion floating />
    </>
  );
}

// From the screen's own address (router.ts, "welcome"), once it's done: an AI's authorization goes on to its allow
// page (or back to the AI, allowed before), a page of the server's that sent the person here opens again, and
// otherwise it's Your trips. True when the page is leaving.
async function goOn(route: Extract<Route, { name: "welcome" }>): Promise<boolean> {
  if (route.oauth) {
    location.assign(await api.continueAuthorization(route.oauth));
    return true;
  }
  if (route.next) {
    location.assign(route.next);
    return true;
  }
  navigate("/", { replace: true });
  return false;
}

// The screen's address opened once it's done: where it would have gone on to, at once, the sunrise showing until
// then; an AI's authorization that has expired since says so.
function OnFromWelcome({ route, onHome }: { route: Extract<Route, { name: "welcome" }>; onHome: () => void }) {
  const [expired, setExpired] = useState<string | null>(null);
  useEffect(() => {
    goOn(route).then(
      (leaving) => {
        if (!leaving) onHome();
      },
      (e: unknown) => setExpired((e as Error).message),
    );
  }, [route, onHome]);
  if (!expired) return <Opening />;
  return (
    <Message
      title="Your AI's sign-in has expired"
      body={expired}
      action={
        <Button
          variant="primary"
          onClick={() => {
            navigate("/", { replace: true });
            onHome();
          }}
        >
          Your trips
        </Button>
      }
    />
  );
}
